import { Component, Prop, State, Event, EventEmitter, Method, h, Host } from '@stencil/core';
import pdfIcon from '../../assets/img/icn/file_icons/icn-PDF.svg';
import pptIcon from '../../assets/img/icn/file_icons/icn-PowerPoint.svg';
import wordIcon from '../../assets/img/icn/file_icons/icn-Word.svg';
import excelIcon from '../../assets/img/icn/file_icons/icn-Excel.svg';
import imageIcon from '../../assets/img/icn/file_icons/icn-Image.svg';
import videoIcon from '../../assets/img/icn/file_icons/icn-Video.svg';
import onenoteIcon from '../../assets/img/icn/file_icons/icn-OneNote.svg';
import defaultIcon from '../../assets/img/icn/file_icons/icn-default.svg';

@Component({
  tag: 'dda-attach-file',
  styleUrls: ['../../global/input.css'],
  shadow: false,
})
export class DdaAttachFile {
  @Prop() label: string;
  @Prop() place_holder: string;
  @Prop() upload_button_text: string;
  @Prop() helper_text: string;
  @Prop() success_text: string;
  @Prop() error_message: string;
  @Prop() size?: string;
  @Prop() validation_type?: string;
  @Prop() validation_id: string = 'text';
  @Prop() input_type?: string;
  @Prop() custom_class?: string = '';
  @Prop() component_mode?: string;
  @Prop() aria_label?: string = '';
  @Prop() button_aria_label?: string = '';
  @Prop() input_id: string;
  @Prop() input_name?: string;
  @Prop() button_id: string;
  @Prop() delete_button_id: string;
  @Prop() button_name?: string;
  @Prop() accept?: string;
  @Prop() disabled: boolean = false;

  @Prop() max_file_size: number = 5; // in MB
  @Prop() file_size_error_message: string = 'File size should not exceed {max} MB';
  
  @State() file: File | null = null;
  @State() isFileUploaded: boolean = false;
  @State() internalErrorMessage: string = '';

  private fileInputRef: HTMLInputElement;

  @Event() fileChanged: EventEmitter<File | null>;

  @Method()
  async getFile(): Promise<File | null> {
    return this.file;
  }

  private fileIcons = {
    pdf: pdfIcon,
    pptx: pptIcon,
    docx: wordIcon,
    xlsx: excelIcon,
    png: imageIcon,
    jpg: imageIcon,
    jpeg: imageIcon,
    mp4: videoIcon,
    mkv: videoIcon,
    one: onenoteIcon,
  };

  handleFileInput(event: Event) {
    console.log(this.fileInputRef.value);
    
    const files = (event.target as HTMLInputElement)?.files;
    if (files && files.length > 0) {
      const selectedFile = files[0];
      const fileSizeInMB = selectedFile.size / (1024 * 1024);

      if (this.max_file_size && fileSizeInMB > this.max_file_size) {
        this.file = null;
        this.isFileUploaded = false;
        this.internalErrorMessage = this.file_size_error_message.replace('{max}', this.max_file_size.toString());
        this.validation_type = 'error';
        this.fileChanged.emit(null);
        return;
      }

      this.file = selectedFile;
      this.isFileUploaded = true;
      this.internalErrorMessage = '';
      this.validation_type = 'normal';
      this.fileChanged.emit(this.file);
    } else {
      this.file = null;
      this.isFileUploaded = false;
      this.internalErrorMessage = '';
      this.validation_type = 'normal';
      this.fileChanged.emit(null);
    }
  }

  removeFile() {
    this.file = null;
    this.isFileUploaded = false;
   if (this.fileInputRef) {
  this.fileInputRef.value = null;
}
    this.internalErrorMessage = '';
    this.validation_type = 'normal';
    this.fileChanged.emit(null);
  }

  getFileIcon(fileName: string) {
    const extension = fileName.split('.').pop()?.toLowerCase();
    return this.fileIcons[extension] || defaultIcon;
  }

  render() {
    const inputClass = [
      'dda-input-container',
      this.validation_type ? `dda-validation-${this.validation_type}` : '',
      this.size ? `dda-input-size-${this.size}` : '',
      this.input_type ? `dda-input-${this.input_type}` : '',
      this.custom_class,
      this.component_mode,
    ].filter(Boolean).join(' ');

    const shouldShowError = this.validation_type === 'error' && (this.internalErrorMessage || this.error_message);

    return (
      <Host>
        <div class={inputClass}>
          {this.label && (
            <span class="dda-input-label" id={`${this.input_id}-label`}>
              {this.label}
            </span>
          )}

          <div class="dda-input-field-group dda-attach-file">
            {this.file ? (
              <div class="dda-file-details">
                <div class="dda-items">
                  <img src={this.getFileIcon(this.file.name)} alt="File Icon" class="dda-file-icon" />
                  <span class="dda-file-name" title={this.file.name}>{this.file.name}</span>
                </div>
                <button
                  type="button"
                  class="remove-file"
                  name={this.button_name}
                  id={this.delete_button_id}
                  role="button"
                  tabIndex={0}
                  aria-label={this.button_aria_label || 'Remove file'}
                  onClick={() => this.removeFile()}
                  disabled={this.disabled}
                >
                  <i class="material-icons material-symbols-outlined" aria-hidden="true">close</i>
                </button>
              </div>
            ) : (
              <div class="dda-file-input">
                <span>{this.place_holder || 'No File Selected'}</span>
                <button
                  type="button"
                  class="dda-file-choose"
                  name={this.button_name}
                  id={this.button_id}
                  tabIndex={0}
                  aria-labelledby={`${this.button_id}-label`}
                  onClick={() => this.fileInputRef?.click()}
                  disabled={this.disabled}
                >
                  {this.upload_button_text || 'Choose File'}
                </button>
                <span id={`${this.button_id}-label`} class="visually-hidden">
                  {this.upload_button_text || 'Choose File'}
                </span>

                <label htmlFor={this.input_id} class="visually-hidden">
                  {this.label}
                </label>
                <input
                  id={this.input_id}
                  name={this.input_name}
                  type="file"
                  accept={this.accept}
                  disabled={this.disabled}
                  hidden
                  ref={(el) => (this.fileInputRef = el as HTMLInputElement)}
                  onInput={(event) => this.handleFileInput(event)}
                />
              </div>
            )}
          </div>

          {shouldShowError ? (
            <span class="dda-error-message" id={this.validation_id} role="alert" aria-live="assertive">
              <span class="dda-d-flex dda-align-items-start dda-gap-2">
                <i class="material-icons material-symbols-outlined" aria-hidden="true">info</i>
                <span>{this.internalErrorMessage || this.error_message}</span>
              </span>
            </span>
          ) : this.isFileUploaded && this.success_text ? (
            <span class="dda-success-text" id={this.validation_id} role="status" aria-live="polite">
              <span class="dda-d-flex dda-align-items-start dda-gap-2">
                <i class="material-icons material-symbols-outlined" aria-hidden="true">check</i>
                <span>{this.success_text}</span>
              </span>
            </span>
          ) : this.helper_text ? (
            <span class="dda-helper-text" id={this.validation_id}>
              <span class="dda-d-flex dda-align-items-start dda-gap-2">
                <i class="material-icons material-symbols-outlined" aria-hidden="true">info</i>
                <span>{this.helper_text}</span>
              </span>
            </span>
          ) : null}
        </div>
      </Host>
    );
  }
}
